import React from 'react'
import ReactDOM from 'react-dom/client'
import { BrowserRouter } from 'react-router-dom'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { asyncWithLDProvider } from 'launchdarkly-react-client-sdk'
import Observability from '@launchdarkly/observability'
import SessionReplay from '@launchdarkly/session-replay'
import { SSOHandler } from './components/SSOHandler'
import { TenantThemeProvider } from './components/TenantThemeProvider'
import { LD_CLIENT_ID, ldContext } from './config/launchdarkly'
import { TENANT_ID } from './config/tenant'
import App from './App.tsx'
import './index.css'
import './i18n'

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      refetchOnWindowFocus: false,
      staleTime: 5 * 60 * 1000, // 5 minutes
    },
  },
})

// When LD_CLIENT_ID is set, asyncWithLDProvider blocks until flags are fetched
// so there is no flash of incorrect feature states on first render.
// When unset (local dev without a key), we fall back to a passthrough wrapper
// and all flags resolve to their default values.
type LDWrapper = React.ComponentType<{ children: React.ReactNode }>
let LDProvider: LDWrapper

if (LD_CLIENT_ID) {
  LDProvider = await asyncWithLDProvider({
    clientSideID: LD_CLIENT_ID,
    context: ldContext(TENANT_ID),
    options: {
      streaming: true,
      plugins: [
        new Observability({
          networkRecording: {
            enabled: true,
            recordHeadersAndBody: true,
          },
          tracingOrigins: [
            /https?:\/\/.*\.escdevelopworks\.com\/api/,
          ],
        }),
        new SessionReplay({
          privacySetting: 'default',
        }),
      ],
    },
  })
} else {
  LDProvider = ({ children }) => <>{children}</>
}

ReactDOM.createRoot(document.getElementById('root')!).render(
  <React.StrictMode>
    <LDProvider>
      <TenantThemeProvider>
        <QueryClientProvider client={queryClient}>
          <BrowserRouter>
            <SSOHandler>
              <App />
            </SSOHandler>
          </BrowserRouter>
        </QueryClientProvider>
      </TenantThemeProvider>
    </LDProvider>
  </React.StrictMode>,
)
